import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAttendanceHistory } from '../api/attendance';
import { getErrorMessage } from '../api/client';
import AttendanceKiosk from '../components/AttendanceKiosk';
import AttendancePhotoThumb from '../components/AttendancePhotoThumb';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config';
import { resolveAttendancePhotoUrl } from '../utils/resolveAttendancePhotoUrl';

export default function AttendancePage() {
  const auth = useAuth();
  const [history, setHistory] = useState([]);
  const [historyFrom, setHistoryFrom] = useState(new Date().toISOString().slice(0, 10));
  const [historyTo, setHistoryTo] = useState(new Date().toISOString().slice(0, 10));
  const [showReport, setShowReport] = useState(false);
  const [reportError, setReportError] = useState('');

  const loadHistory = useCallback(async () => {
    if (!auth.token) return;
    setReportError('');
    try {
      const data = await fetchAttendanceHistory(auth.token, {
        from: historyFrom,
        to: historyTo
      });
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setReportError(getErrorMessage(err));
    }
  }, [auth.token, historyFrom, historyTo]);

  useEffect(() => {
    if (showReport && auth.token) loadHistory();
  }, [showReport, auth.token, loadHistory]);

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-indigo-600 hover:underline">Back to Store</Link>
            <h1 className="text-xl font-bold">Attendance</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        <div className="flex gap-2 overflow-x-auto border-b">
          <button
            type="button"
            onClick={() => setShowReport(false)}
            className={`-mb-px shrink-0 border-b-2 px-4 py-2 text-sm font-medium ${!showReport ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'}`}
          >
            Kiosk
          </button>
          {auth.token && (
            <button
              type="button"
              onClick={() => setShowReport(true)}
              className={`-mb-px shrink-0 border-b-2 px-4 py-2 text-sm font-medium ${showReport ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-600'}`}
            >
              Report
            </button>
          )}
        </div>

        {!showReport && <AttendanceKiosk />}

        {showReport && auth.token && (
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Attendance history</h2>
            {reportError ? <p className="mb-3 text-red-600">{reportError}</p> : null}
            <div className="mb-4 flex flex-wrap gap-2">
              <input
                type="date"
                value={historyFrom}
                onChange={(e) => setHistoryFrom(e.target.value)}
                className="rounded border px-2 py-1"
                aria-label="From date"
              />
              <input
                type="date"
                value={historyTo}
                onChange={(e) => setHistoryTo(e.target.value)}
                className="rounded border px-2 py-1"
                aria-label="To date"
              />
              <button type="button" onClick={loadHistory} className="rounded bg-indigo-600 px-3 py-1 text-sm text-white">
                Load
              </button>
            </div>

            <div className="space-y-3 pb-2 md:hidden">
              {history.length === 0 && (
                <p className="py-6 text-center text-gray-500">No attendance records for this period</p>
              )}
              {history.map((r) => {
                const photoSrc = r.facePhotoUrl ? resolveAttendancePhotoUrl(r.facePhotoUrl, API_BASE_URL) : '';
                const reportPhotoSrc = r.reportPhotoUrl ? resolveAttendancePhotoUrl(r.reportPhotoUrl, API_BASE_URL) : '';
                const when = r.timestamp ? new Date(r.timestamp).toLocaleString() : '-';
                const locLine =
                  r.latitude != null && r.longitude != null
                    ? `${Number(r.latitude).toFixed(4)}, ${Number(r.longitude).toFixed(4)}`
                    : null;
                const locTime =
                  r.locationCapturedAt ? new Date(r.locationCapturedAt).toLocaleString() : null;
                return (
                  <div
                    key={r._id}
                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-gray-900">
                        {r.employeeId?.name || r.employeeId || '-'}
                      </span>
                      <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                        {r.type === 'in' ? 'Clock In' : 'Clock Out'}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{when}</p>
                    {locLine ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {locLine}
                        {locTime ? <span className="mt-0.5 block">GPS: {locTime}</span> : null}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-400">No GPS on record</p>
                    )}
                    {r.workNote ? (
                      <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                        <p className="text-xs font-medium uppercase text-gray-500">Work note</p>
                        <p className="mt-1 whitespace-pre-wrap">{r.workNote}</p>
                      </div>
                    ) : null}
                    {reportPhotoSrc ? (
                      <div className="mt-3">
                        <p className="mb-2 text-xs font-medium uppercase text-gray-500">Report photo</p>
                        <AttendancePhotoThumb
                          src={reportPhotoSrc}
                          caption="Work report"
                          altText={`Work report photo - ${when}`}
                        />
                      </div>
                    ) : null}
                    {photoSrc ? (
                      <div className="mt-4 flex justify-center border-t border-gray-100 pt-4">
                        <AttendancePhotoThumb
                          src={photoSrc}
                          caption={when}
                          altText={`Attendance photo - ${when}`}
                        />
                      </div>
                    ) : (
                      <p className="mt-3 text-center text-sm text-gray-400">No photo</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="hidden max-h-96 overflow-x-auto overflow-y-auto touch-pan-x md:block">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="p-2 text-left">Employee</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Time</th>
                    <th className="min-w-[220px] p-2 text-left">Work Note</th>
                    <th className="p-2 text-left">Location</th>
                    <th className="w-[140px] p-2 text-left">Photo</th>
                    <th className="w-[140px] p-2 text-left">Report Photo</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-gray-500">
                        No attendance records for this period
                      </td>
                    </tr>
                  )}
                  {history.map((r) => (
                    <tr key={r._id} className="border-b">
                      <td className="p-2">{r.employeeId?.name || r.employeeId || '-'}</td>
                      <td className="p-2">{r.type}</td>
                      <td className="p-2">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '-'}</td>
                      <td className="whitespace-pre-wrap p-2 text-xs">{r.workNote || '-'}</td>
                      <td className="p-2 text-xs">
                        {r.latitude != null && r.longitude != null ? (
                          <span>
                            {Number(r.latitude).toFixed(4)}, {Number(r.longitude).toFixed(4)}
                            {r.locationCapturedAt ? (
                              <span className="mt-0.5 block text-gray-500">
                                GPS: {new Date(r.locationCapturedAt).toLocaleString()}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="p-2 align-top">
                        {r.facePhotoUrl ? (
                          <AttendancePhotoThumb
                            src={resolveAttendancePhotoUrl(r.facePhotoUrl, API_BASE_URL)}
                            caption={
                              r.timestamp ? new Date(r.timestamp).toLocaleString() : undefined
                            }
                            altText="Attendance verification photo"
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="p-2 align-top">
                        {r.reportPhotoUrl ? (
                          <AttendancePhotoThumb
                            src={resolveAttendancePhotoUrl(r.reportPhotoUrl, API_BASE_URL)}
                            caption="Work report"
                            altText="Work report photo"
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
